// Public dashboard — landing des créateurs TikTok loggés via Login Kit mais NON
// rattachés à une agence Black. Adapté du design Black OS, sur les primitives
// NoiRock, avec les données TikTok déjà récupérées par tiktok-auth
// (localStorage: creator_profile + creator_tiktok). Sans Coach/Wiki (hors scope).
// i18n via creatorLang (en/fr/it/de) — picker de langue au 1er login.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { t, getLang, hasChosenLang } from '../../utils/creatorLang';
import LanguageSelector from '../../components/LanguageSelector';
import {
  CreatorScreen, ScreenHeader, Card, StatTile, QuickLink, SectionLabel, BottomNav, I,
} from '../../components/creator/primitives';
import { PublicSettingsButton } from '../../components/creator/PublicSettingsButton';

const fmtStat = (n) => {
  if (n == null) return '—';
  const v = Number(n);
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1) + 'M';
  if (v >= 1_000) return (v / 1_000).toFixed(1) + 'k';
  return String(v);
};

const PublicDashboard = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [videos, setVideos] = useState([]);
  const [bio, setBio] = useState(null);
  const [, setLangTick] = useState(getLang());                 // re-render au changement de langue
  const [showLangPicker, setShowLangPicker] = useState(false);

  useEffect(() => {
    // 1er login : choisir la langue AVANT d'afficher le contenu (stockée en préférence).
    if (!hasChosenLang()) setShowLangPicker(true);
    let cancelled = false;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (cancelled) return;
      if (!user || user.user_metadata?.role !== 'creator') { navigate('/my/login', { replace: true }); return; }
      if (user.user_metadata?.known !== false) { navigate('/my/dashboard', { replace: true }); return; }
      try {
        const p = JSON.parse(localStorage.getItem('creator_profile') || 'null');
        setProfile(p || { display_name: user.user_metadata?.tiktok_display_name, avatar_url: user.user_metadata?.tiktok_avatar_url });
      } catch { /* ignore */ }
      try {
        const tk = JSON.parse(localStorage.getItem('creator_tiktok') || 'null');
        if (tk) { setStats(tk.stats || null); setVideos(Array.isArray(tk.videos) ? tk.videos : []); setBio(tk.bio || null); }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const name = profile?.display_name || profile?.username || t('pub_creator');

  return (
    <CreatorScreen>
      <ScreenHeader title={name} subtitle={t('pub_dash_subtitle')} right={<PublicSettingsButton />} />

      <div style={{ padding: '16px 20px 96px', display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* Profil TikTok */}
        <Card pad={16}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            {profile?.avatar_url
              ? <img src={profile.avatar_url} alt="" style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover' }} />
              : <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--bg-3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--fg-3)', fontSize: 22, fontWeight: 700 }}>{name.slice(0, 1).toUpperCase()}</div>}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--fg-0)' }}>{name}</div>
              {profile?.username && <div style={{ fontSize: 12, color: 'var(--fg-3)' }}>@{profile.username}</div>}
              {bio && <div style={{ marginTop: 8, fontSize: 12, color: 'var(--fg-2)', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{bio}</div>}
            </div>
          </div>
        </Card>

        {/* Stats publiques (scope user.info.stats) */}
        {stats && (
          <div>
            <SectionLabel>{t('pub_stats_label')}</SectionLabel>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
              <StatTile label={t('pub_followers')} value={fmtStat(stats.follower_count)} />
              <StatTile label={t('pub_following')} value={fmtStat(stats.following_count)} />
              <StatTile label={t('pub_likes')} value={fmtStat(stats.likes_count)} />
              <StatTile label={t('pub_videos')} value={fmtStat(stats.video_count)} />
            </div>
          </div>
        )}

        {/* Vidéos récentes (scope video.list) */}
        {videos.length > 0 && (
          <div>
            <SectionLabel>{t('pub_recent_videos')}</SectionLabel>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              {videos.slice(0, 6).map((v) => (
                <a key={v.id} href={v.share_url} target="_blank" rel="noopener noreferrer"
                   style={{ position: 'relative', display: 'block', borderRadius: 10, overflow: 'hidden', background: 'var(--bg-3)', aspectRatio: '9 / 16' }}>
                  {v.cover_image_url && <img src={v.cover_image_url} alt="" loading="lazy" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />}
                  <span style={{ position: 'absolute', left: 6, bottom: 6, color: '#fff', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3, textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>
                    <I.Eye size={11} stroke="currentColor" />{fmtStat(v.view_count)}
                  </span>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Explore */}
        <div>
          <SectionLabel>{t('pub_explore')}</SectionLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <QuickLink icon={<I.Diamond size={18} stroke="var(--accent-cyan)" />} title={t('pub_calc_title')} desc={t('pub_calc_link_desc')} onClick={() => navigate('/my/calculator')} tint="cyan" />
            <QuickLink icon={<I.Trophy size={18} stroke="var(--accent-pink)" />} title={t('pub_agency_highlights')} desc={t('pub_agency_link_desc')} onClick={() => navigate('/my/agency-stats')} tint="pink" />
          </div>
        </div>

        {/* Rejoindre l'agence */}
        <div>
          <SectionLabel>{t('pub_partner')}</SectionLabel>
          <QuickLink icon={<I.Sparkle size={18} stroke="var(--accent-pink)" />} title={t('pub_join_title')} desc={t('pub_join_desc')} onClick={() => navigate('/my/apply')} tint="pink" />
        </div>
      </div>

      <LanguageSelector show={showLangPicker} onDone={() => { setShowLangPicker(false); setLangTick(getLang()); }} />

      <BottomNav active="public-dashboard" variant="public" />
    </CreatorScreen>
  );
};

export default PublicDashboard;
